import { useCallback } from 'react';
import trim from 'lodash/trim';
import isEmpty from 'validator/lib/isEmpty';

import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import setTouchedForErrors from '@/components/Form/utils/setTouchedForErrors';
import useNavigation from '@/hooks/useNavigation';
import adaptInstruction from '@/modules/corporate/helpers/adaptInstruction';
import getEditorSuccessMessage from '@/modules/corporate/helpers/getEditorSuccessMessage';
import useUpdateContentStatus from '@/modules/corporate/modules/admin/hooks/useUpdateContentStatus';
import { useEmbeddedExerciseEditor } from '@/modules/corporate/modules/course/components/ExerciseCardContainer/components/ExerciseEditorModal/context';
import { adaptTagsOnSubmit } from '@/modules/corporate/modules/tags/utils';
import routes from '@/pages/corporate/routes/contents/exercise';
import {
  CreateExerciseInput,
  PublishContentConfig,
  ResourceReviewTypeEnum,
  ResourceStatusEnum,
  ResourceTypeEnum,
  UpdateExerciseInput,
} from '@/types/schema';

import { validateFormForPreview } from '../components/Editor/config';
import { adaptReviewerConfigOnSubmit } from '../components/SubmissionReview/utils';
import { useCreateExerciseMutation } from '../graphql/mutations/generated/createExercise';
import { useUpdateExerciseMutation } from '../graphql/mutations/generated/updateExercise';
import adaptCertificateSettingsOnSubmit from '../helpers/adaptCertificateSettingsOnSubmit';

export default function useExerciseActions({
  exerciseId,
  onSuccessCb,
  setExerciseId,
  formRef,
  onCreated,
}: {
  exerciseId?: string;
  onSuccessCb?: () => void;
  setExerciseId: (id?: string) => void;
  formRef: React.RefObject<any>;
  onCreated?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const embeddedEditor = useEmbeddedExerciseEditor();
  const [createdExerciseResult, createExercise] = useCreateExerciseMutation();
  const [updatedExerciseResult, updateExercise] = useUpdateExerciseMutation();
  const { onUpdateContentStatus } = useUpdateContentStatus();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const navigation = useNavigation();

  const onSuccess = useCallback(
    (id: string, message: string, publish: boolean, isShowingSnackbar: boolean) => {
      if (isShowingSnackbar) {
        showSnackbar({
          message,
        });
      }

      if (publish) {
        if (embeddedEditor) {
          // Ensure the modal has the id before onClose triggers handleClose → updateAttribute.
          onCreated?.(id);
          embeddedEditor.onClose();
        } else {
          navigation.replace(routes.AdminExerciseDetails(id));
        }
      } else if (!exerciseId) {
        setExerciseId(id);
        // Notify the embedding container (e.g. ExerciseEditorModal) of the new id.
        // Fires in both embedded and standalone mode so the modal can track it
        // without immediately writing to the ProseMirror block attribute.
        onCreated?.(id);
      }
      onSuccessCb?.();
    },
    [embeddedEditor, exerciseId, navigation, onCreated, onSuccessCb, setExerciseId, showSnackbar],
  );

  const onError = useCallback(() => {
    showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
  }, [showErrorSnackbar, t]);

  const onSubmit = useCallback(
    async (values: any, publish: boolean, showSnackbar: boolean = true) => {
      const {
        name,
        objective,
        exerciseQuestions,
        instruction,
        advanceSettings,
        scorecardIds,
        tags,
        duration,
        cover,
        reviewerInstruction,
        certificateSettings,
        skillIds,
        reviewType,
        reviewerConfig,
        subWorkspaceIds,
        publishConfig,
        assignmentRestriction,
      } = values;
      const input = {
        name,
        objective,
        exerciseQuestions,
        duration,
        cover,
        instruction: adaptInstruction(instruction),
        advanceSettings,
        tags: adaptTagsOnSubmit(tags),
        scorecardIds: reviewType === ResourceReviewTypeEnum.NoReview ? undefined : scorecardIds,
        reviewerInstruction:
          reviewType === ResourceReviewTypeEnum.NoReview
            ? undefined
            : adaptInstruction(reviewerInstruction),
        certificateSettings: certificateSettings?.certificateTemplateId
          ? adaptCertificateSettingsOnSubmit(certificateSettings, reviewType)
          : null,
        skillIds,
        reviewType,
        reviewerConfig: adaptReviewerConfigOnSubmit(reviewType, reviewerConfig),
        subWorkspaceIds,
        maintainDraftState: true,
        assignmentRestriction,
      };

      const onCreate = async () => {
        const createdExercise = await createExercise({
          input: {
            ...input,
            publish,
          } as CreateExerciseInput,
        });

        createdExercise.data?.createExercise.success
          ? onSuccess(
              createdExercise.data?.createExercise.exercise?.id!,
              getEditorSuccessMessage({
                t,
                publishConfig,
                type: ResourceTypeEnum.Exercise,
                contentId: exerciseId,
              }),
              publish,
              showSnackbar,
            )
          : onError();

        return createdExercise.data?.createExercise.exercise?.id;
      };

      const onUpdate = async () => {
        if (status === ResourceStatusEnum.Archived) {
          await onUpdateContentStatus(exerciseId!, ResourceStatusEnum.Published);
        }

        const updatedExercise = await updateExercise({
          input: { resourceId: exerciseId, ...input, publishConfig } as UpdateExerciseInput,
        });

        updatedExercise.data?.updateExercise.success
          ? onSuccess(
              updatedExercise.data?.updateExercise.exercise?.id!,
              getEditorSuccessMessage({
                t,
                publishConfig,
                type: ResourceTypeEnum.Exercise,
                contentId: exerciseId,
              }),
              publish,
              showSnackbar,
            )
          : onError();

        return updatedExercise.data?.updateExercise.exercise?.id;
      };

      return exerciseId ? await onUpdate() : await onCreate();
    },
    [exerciseId, createExercise, onSuccess, t, onError, updateExercise, onUpdateContentStatus],
  );

  const _onSubmit = useCallback(
    (values: any) => {
      onSubmit(values, true);
    },
    [onSubmit],
  );

  const onSaveDraft = useCallback(
    async (values: any) => {
      const { name } = values;
      await onSubmit({ ...values, name: isEmpty(trim(name)) ? t('Common:Untitled') : name }, false);
    },
    [onSubmit, t],
  );

  const onSaveDraftAndPreview = useCallback(
    async (values: any) => {
      const errors = validateFormForPreview(t, values);
      if (setTouchedForErrors(errors, formRef.current.setTouched)) return;

      if (formRef.current.dirty) {
        const newExerciseId = await onSubmit({ ...values }, false, false);
        if (!newExerciseId) return;

        await navigation.replace(routes.ExerciseEditor(newExerciseId));
        navigation.push(routes.AdminExercisePreviewDevices(newExerciseId));
      } else if (exerciseId) {
        navigation.push(routes.AdminExercisePreviewDevices(exerciseId));
      }
    },
    [exerciseId, formRef, navigation, onSubmit, t],
  );

  const onPublish = useCallback(
    (values: any, publishConfig: PublishContentConfig) => {
      onSubmit({ ...values, publishConfig }, true);
    },
    [onSubmit],
  );

  return {
    onSubmit: _onSubmit,
    fetching: createdExerciseResult.fetching || updatedExerciseResult.fetching,
    onSaveDraft,
    onSaveDraftAndPreview,
    onPublish,
  };
}
